import { useCallback, memo } from 'react';
import { Stack, Text, Flex, Box } from '@chakra-ui/react';
import { CalendarBlank, Clock } from '@phosphor-icons/react';
import ReactPlayer from 'react-player';

import TranscriptList from '@/views/Interview/Copilot/Details/TranscriptList';
import VideoControls from '@/components/media/VideoControls';
import { useVideoPlayer } from '@/hooks/useVideoPlayer';
import { Meeting } from '@/types/Meeting';

interface InterviewTranscriptSectionProps {
  meeting: Meeting;
}

const InterviewTranscriptSection = memo(({ meeting }: InterviewTranscriptSectionProps) => {
  const {
    playing,
    played,
    loaded,
    duration,
    smoothProgress,
    error,
    playerRef,
    setPlaying,
    handleSeek,
    handleError,
    handleEnded,
    setIsReady,
    setPlayed,
    setLoaded,
    setDuration
  } = useVideoPlayer();

  const getMeetingTime = useCallback(() => {
    return new Date(meeting.meeting_start_time * 1000);
  }, [meeting.meeting_start_time]);

  const getMeetingDuration = useCallback(() => {
    return Math.round((meeting.timestamp.ended_at - meeting.timestamp.recorded_at) / 60);
  }, [meeting.timestamp.ended_at, meeting.timestamp.recorded_at]);

  return (
    <Stack>
      <Text fontSize="24px" fontWeight="bold">
        {meeting.title}
      </Text>
      <Flex gap="8px" align="center" color="neutrals.700">
        <CalendarBlank size={20} color="#6B7280" aria-hidden="true" />
        {getMeetingTime().toLocaleDateString('default', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })}
        <Text>·</Text>
        <Clock size={20} color="#6B7280" aria-hidden="true" />
        {getMeetingTime().toLocaleTimeString('default', {
          hour: 'numeric',
          minute: 'numeric'
        })}
        <Text>·</Text>
        <Text>{getMeetingDuration()} mins</Text>
      </Flex>
      <Box maxH="500px" overflowY="auto">
        <TranscriptList meetingId={meeting.meeting_id} transcripts={meeting.transcripts} />
      </Box>
      <Box
        w="100%"
        h="115px"
        border="1px solid"
        borderColor="neutrals.200"
        borderRadius="12px"
        p="16px"
      >
        <ReactPlayer
          url={meeting.video_url}
          playing={playing}
          volume={1}
          onProgress={({ playedSeconds }) => setPlayed(playedSeconds)}
          onDuration={(duration) => {
            if (duration !== Infinity) {
              setLoaded(duration);
              setDuration(duration);
            }
          }}
          onReady={() => setIsReady(true)}
          onEnded={handleEnded}
          onError={handleError}
          ref={playerRef}
          config={{
            file: {
              forceVideo: false,
              forceAudio: true
            }
          }}
          style={{
            display: 'none'
          }}
        />
        {error ? (
          <Text color="red.500" textAlign="center">
            {error}
          </Text>
        ) : (
          <VideoControls
            playing={playing}
            onPlayPause={() => {
              setPlaying(!playing);
            }}
            onSeek={handleSeek}
            played={played}
            loaded={loaded}
            duration={duration}
            smoothProgress={smoothProgress}
          />
        )}
      </Box>
    </Stack>
  );
});

InterviewTranscriptSection.displayName = 'InterviewTranscriptSection';

export default InterviewTranscriptSection;
